import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Button, StatCard, Table, useTable } from "@repo/o37-ui";
import {
  Plus,
  Calculator,
  CheckCircle,
  Clock,
  Archive,
  FileText,
  TrendingUp,
  TrendingDown
} from "lucide-react";
import { HoldingsCostPlan, formatCents, HOLDINGS_PLAN_STATUSES } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function PlanList() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<HoldingsCostPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Create form state
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanDescription, setNewPlanDescription] = useState('');
  const [newPlanMonths, setNewPlanMonths] = useState(12);
  const [creating, setCreating] = useState(false);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/plans`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setPlans(data.map((p: any) => ({
          id: p.id,
          name: p.name,
          description: p.description,
          timeHorizonMonths: p.time_horizon_months,
          startDate: p.start_date,
          endDate: p.end_date,
          status: p.status,
          approvedBy: p.approved_by,
          approvedAt: p.approved_at,
          executedAt: p.executed_at,
          notes: p.notes,
          createdBy: p.created_by,
          createdAt: p.created_at,
          totalMonthlyImpact: p.total_monthly_impact || 0,
          totalAnnualImpact: p.total_annual_impact || 0,
          lineItemCount: p.line_item_count || 0
        })));
      }
    } catch (e) {
      console.error('Failed to load plans:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newPlanName.trim()) return;

    setCreating(true);
    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + newPlanMonths);

      const response = await fetch(`${API_URL}/holdings/plans`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({
          name: newPlanName.trim(),
          description: newPlanDescription.trim() || null,
          timeHorizonMonths: newPlanMonths,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        })
      });

      if (response.ok) {
        const newPlan = await response.json();
        setShowCreateForm(false);
        setNewPlanName('');
        setNewPlanDescription('');
        setNewPlanMonths(12);
        navigate(`/holdings/planning/${newPlan.id}`);
      }
    } catch (e) {
      console.error('Failed to create plan:', e);
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = HOLDINGS_PLAN_STATUSES.find(s => s.status === status);
    const colorClass = statusConfig?.color || 'bg-neutral-100 text-neutral-700';
    const label = statusConfig?.label || status;

    const icons: Record<string, typeof Clock> = {
      draft: FileText,
      approved: CheckCircle,
      executed: TrendingUp,
      archived: Archive
    };
    const Icon = icons[status] || Clock;

    return (
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" />
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${colorClass}`}>
          {label}
        </span>
      </div>
    );
  };

  // Stats
  const draftPlans = plans.filter(p => p.status === 'draft').length;
  const approvedPlans = plans.filter(p => p.status === 'approved').length;
  const totalPlannedMonthly = plans
    .filter(p => p.status === 'approved' || p.status === 'draft')
    .reduce((sum, p) => sum + (p.totalMonthlyImpact || 0), 0);

  const columns = useMemo(() => [
    {
      id: "name",
      header: "Plan",
      cell: ({ row }: { row: { original: HoldingsCostPlan } }) => (
        <div>
          <div className="font-medium text-neutral-900">{row.original.name}</div>
          {row.original.description && (
            <div className="text-sm text-neutral-500 truncate max-w-xs">
              {row.original.description}
            </div>
          )}
        </div>
      )
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }: { row: { original: HoldingsCostPlan } }) => getStatusBadge(row.original.status)
    },
    {
      id: "horizon",
      header: "Time Horizon",
      cell: ({ row }: { row: { original: HoldingsCostPlan } }) => (
        <div className="text-sm">
          <div className="font-medium text-neutral-900">
            {row.original.timeHorizonMonths} months
          </div>
          <div className="text-neutral-500">
            {formatDate(row.original.startDate)} - {row.original.endDate ? formatDate(row.original.endDate) : 'Ongoing'}
          </div>
        </div>
      )
    },
    {
      id: "impact",
      header: "Monthly Impact",
      cell: ({ row }: { row: { original: HoldingsCostPlan } }) => {
        const impact = row.original.totalMonthlyImpact || 0;
        const isPositive = impact > 0;
        return (
          <div className="flex items-center gap-1.5">
            {impact !== 0 && (
              isPositive ? (
                <TrendingUp className="w-4 h-4 text-red-500" />
              ) : (
                <TrendingDown className="w-4 h-4 text-emerald-500" />
              )
            )}
            <span className={`font-medium ${
              impact > 0 ? 'text-red-600' : impact < 0 ? 'text-emerald-600' : 'text-neutral-500'
            }`}>
              {impact > 0 ? '+' : ''}{formatCents(impact)}/mo
            </span>
          </div>
        );
      }
    },
    {
      id: "items",
      header: "Line Items",
      cell: ({ row }: { row: { original: HoldingsCostPlan } }) => (
        <span className="text-sm text-neutral-600">
          {row.original.lineItemCount || 0} items
        </span>
      )
    },
    {
      id: "actions",
      header: "",
      size: 120,
      cell: ({ row }: { row: { original: HoldingsCostPlan } }) => (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-8 text-xs"
            onClick={() => navigate(`/holdings/planning/${row.original.id}`)}
            text="View"
          />
          {row.original.status !== 'archived' && (
            <Button
              variant="outline"
              className="h-8 text-xs"
              onClick={() => navigate(`/holdings/planning/${row.original.id}/compare`)}
              text="Compare"
            />
          )}
        </div>
      )
    }
  ], [navigate]);

  const { table, ...tableProps } = useTable({
    data: plans,
    columns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: plans.length,
    resourceName: (plural) => `plan${plural ? "s" : ""}`,
  });

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Cost Planning</h1>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Cost Planning</h1>
          <p className="text-neutral-500">Plan and model future cost changes</p>
        </div>
        <Button
          onClick={() => setShowCreateForm(true)}
          icon={<Plus className="w-4 h-4" />}
          text="New Plan"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total Plans"
          value={plans.length.toString()}
          hint="All time"
        />
        <StatCard
          label="Draft"
          value={draftPlans.toString()}
          hint="In progress"
        />
        <StatCard
          label="Approved"
          value={approvedPlans.toString()}
          hint="Ready to execute"
        />
        <StatCard
          label="Planned Change"
          value={formatCents(totalPlannedMonthly)}
          hint="Monthly impact"
        />
      </div>

      {/* Create Plan Form */}
      {showCreateForm && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h3 className="font-semibold text-neutral-900 mb-4">Create New Plan</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Plan Name *
                </label>
                <input
                  type="text"
                  value={newPlanName}
                  onChange={(e) => setNewPlanName(e.target.value)}
                  placeholder="e.g., Q2 2025 Cost Optimization"
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Time Horizon
                </label>
                <select
                  value={newPlanMonths}
                  onChange={(e) => setNewPlanMonths(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={3}>3 months</option>
                  <option value={6}>6 months</option>
                  <option value={12}>12 months</option>
                  <option value={24}>24 months</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={newPlanDescription}
                  onChange={(e) => setNewPlanDescription(e.target.value)}
                  placeholder="Optional description..."
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-neutral-100">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewPlanName('');
                  setNewPlanDescription('');
                  setNewPlanMonths(12);
                }}
                text="Cancel"
              />
              <Button
                onClick={handleCreate}
                disabled={creating || !newPlanName.trim()}
                text={creating ? 'Creating...' : 'Create Plan'}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans Table */}
      <Card>
        <CardContent className="p-0">
          {plans.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <Calculator className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <h3 className="text-lg font-medium mb-2">No Cost Plans</h3>
              <p className="mb-6">Create your first plan to model future cost changes.</p>
              <Button
                onClick={() => setShowCreateForm(true)}
                icon={<Plus className="w-4 h-4" />}
                text="Create Plan"
              />
            </div>
          ) : (
            <Table {...tableProps} table={table} />
          )}
        </CardContent>
      </Card>

      {/* Help Text */}
      <Card className="mt-6">
        <CardContent className="p-6">
          <h3 className="font-semibold text-neutral-900 mb-3">About Cost Planning</h3>
          <div className="text-sm text-neutral-600 space-y-2">
            <p>
              Cost plans help you model and forecast changes to your recurring costs before they happen.
              Create line items for new subscriptions, modifications, cancellations, or headcount changes.
            </p>
            <p>
              Once approved, you can execute a plan to apply the changes, or compare it against your
              current run rate to see the projected impact.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
